import React, { useState, useMemo, useEffect } from "react";
import logoNeron from "../assets/NeronEnergia_logo.png";
import logoNeronClaro from "../assets/NeronPanelClaro.png";
import logoLogin from "../assets/NeronLogin.png";
import fondoLogin from "../assets/fondologin.png";
import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || "";
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_KEY || "";
const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

// ── ESTILOS GLOBALES ───────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500;600&family=DM+Sans:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg:       #090d13;
    --surface:  #111722;
    --surface2: #161e2c;
    --border:   rgba(255,255,255,.08);
    --border2:  rgba(255,255,255,.12);
    --text:     #f0f4ff;
    --text2:    #8a9bb8;
    --text3:    #4a5a72;
    --accent:   #f5a623;
    --green:    #00e5a0;
    --red:      #ff5b5b;
    --blue:     #4d9fff;
    --display:  'Outfit', sans-serif;
    --body:     'DM Sans', sans-serif;
    --mono:     'IBM Plex Mono', monospace;
    --radius:   12px;
  }

  .tema-claro {
    --bg:       #eef2f7;
    --surface:  #ffffff;
    --surface2: #e2e8f0;
    --border:   rgba(0,0,0,.10);
    --border2:  rgba(0,0,0,.15);
    --text:     #0f1923;
    --text2:    #364a63;
    --text3:    #6b7e99;
    --accent:   #c47000;
    --green:    #007a54;
    --red:      #c02020;
    --blue:     #1a60b0;
    --display:  'Outfit', sans-serif;
    --body:     'DM Sans', sans-serif;
    --mono:     'IBM Plex Mono', monospace;
    --radius:   12px;
  }

  /* Fondo del body en tema claro */
  body.tema-claro { background: #eef2f7; }

  /* Sidebar en tema claro */
  body.tema-claro .sidebar { background: #ffffff; border-right: 1px solid rgba(0,0,0,.10); }
  body.tema-claro .sidebar-brand { color: #0f1923; }
  body.tema-claro .panel-topbar { background: rgba(238,242,247,.9); }
  body.tema-claro .panel-main { background: #eef2f7; }

  /* Nombre Nerón en tema claro — el texto blanco se hace oscuro */
  body.tema-claro .sidebar-brand span { color: var(--accent); }
  body.tema-claro .brand-name { color: #0f1923; }
  body.tema-claro .topbar-badge { background: #e2e8f0; color: #364a63; }

  body { background: var(--bg); color: var(--text); font-family: var(--body); }

  /* SCROLLBAR */
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: var(--surface); }
  ::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 3px; }

  /* PANEL LAYOUT */
  .panel-wrapper { display: flex; min-height: 100vh; }

  /* SIDEBAR */
  .sidebar {
    width: 220px; flex-shrink: 0;
    background: var(--surface);
    border-right: 1px solid var(--border);
    display: flex; flex-direction: column;
    position: sticky; top: 0; height: 100vh;
    overflow-y: auto;
  }
  .sidebar-logo {
    padding: 24px 20px 20px;
    border-bottom: 1px solid var(--border);
  }
  .sidebar-brand {
    font-family: var(--display); font-size: 16px; font-weight: 800;
    letter-spacing: -0.02em;
  }
  .sidebar-brand span { color: var(--accent); }
  .sidebar-sub {
    font-family: var(--mono); font-size: 9px; color: var(--text3);
    letter-spacing: 0.1em; text-transform: uppercase; margin-top: 3px;
  }
  .sidebar-nav { padding: 12px 10px; flex: 1; }
  .nav-section {
    font-family: var(--mono); font-size: 9px; color: var(--text3);
    letter-spacing: 0.12em; text-transform: uppercase;
    padding: 12px 10px 6px;
  }
  .nav-item {
    display: flex; align-items: center; gap: 10px;
    padding: 9px 12px; border-radius: 8px; cursor: pointer;
    font-size: 13px; font-weight: 500; color: var(--text2);
    transition: all .15s; margin-bottom: 2px; border: none;
    background: none; width: 100%; text-align: left;
  }
  .nav-item:hover { background: rgba(255,255,255,.05); color: var(--text); }
  .nav-item.active { background: rgba(245,166,35,.1); color: var(--accent); }
  .nav-item .nav-icon { font-size: 15px; width: 20px; text-align: center; }
  .sidebar-footer {
    padding: 16px; border-top: 1px solid var(--border);
  }
  .logout-btn {
    width: 100%; padding: 8px 12px; border-radius: 8px;
    border: 1px solid var(--border2); background: none;
    color: var(--text2); font-family: var(--body); font-size: 13px;
    cursor: pointer; transition: all .15s;
  }
  .logout-btn:hover { border-color: var(--red); color: var(--red); }

  /* TEMA TOGGLE */
  .theme-toggle {
    width: 100%; padding: 9px 12px; border-radius: 8px;
    border: 1px solid var(--border2); background: var(--surface2);
    color: var(--text); font-family: var(--body); font-size: 13px;
    cursor: pointer; transition: all .15s; margin-bottom: 8px;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    font-weight: 600;
  }
  .theme-toggle:hover { border-color: var(--accent); color: var(--accent); }

  /* MAIN */
  .panel-main { flex: 1; display: flex; flex-direction: column; overflow: hidden; }
  .panel-topbar {
    padding: 16px 28px; border-bottom: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between;
    background: rgba(9,13,19,.8); backdrop-filter: blur(12px);
    position: sticky; top: 0; z-index: 10;
  }
  .topbar-title {
    font-family: var(--display); font-size: 18px; font-weight: 700;
    letter-spacing: -0.02em;
  }
  .topbar-badge {
    font-family: var(--mono); font-size: 10px; color: var(--text3);
    background: var(--surface2); border: 1px solid var(--border);
    border-radius: 6px; padding: 3px 10px;
  }
  .panel-content { padding: 28px; flex: 1; overflow-y: auto; }

  /* STAT CARDS */
  .stats-grid {
    display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 14px; margin-bottom: 28px;
  }
  .stat-card {
    background: var(--surface); border: 1px solid var(--border);
    border-radius: var(--radius); padding: 18px 20px;
  }
  .stat-label {
    font-family: var(--mono); font-size: 10px; color: var(--text3);
    letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 8px;
  }
  .stat-value {
    font-family: var(--display); font-size: 28px; font-weight: 800;
    letter-spacing: -0.02em;
  }
  .stat-value.accent { color: var(--accent); }
  .stat-value.green { color: var(--green); }
  .stat-sub { font-size: 11px; color: var(--text3); margin-top: 4px; }

  /* TOOLBAR */
  .toolbar {
    display: flex; gap: 10px; align-items: center;
    margin-bottom: 16px; flex-wrap: wrap;
  }
  .search-box {
    flex: 1; min-width: 200px;
    background: var(--surface); border: 1px solid var(--border2);
    border-radius: 8px; padding: 9px 14px;
    color: var(--text); font-family: var(--body); font-size: 13px;
    outline: none; transition: border-color .2s;
  }
  .search-box:focus { border-color: var(--accent); }
  .search-box::placeholder { color: var(--text3); }
  .filter-select {
    background: var(--surface); border: 1px solid var(--border2);
    border-radius: 8px; padding: 9px 12px;
    color: var(--text2); font-family: var(--mono); font-size: 11px;
    outline: none; cursor: pointer;
  }
  .btn-export {
    background: rgba(0,229,160,.1); border: 1px solid rgba(0,229,160,.3);
    color: var(--green); border-radius: 8px; padding: 9px 16px;
    font-family: var(--mono); font-size: 11px; font-weight: 600;
    cursor: pointer; transition: all .15s; white-space: nowrap;
  }
  .btn-export:hover { background: rgba(0,229,160,.2); }

  /* TABLE */
  .table-wrapper {
    background: var(--surface); border: 1px solid var(--border);
    border-radius: var(--radius); overflow: hidden;
  }
  .leads-table { width: 100%; border-collapse: collapse; }
  .leads-table th {
    padding: 11px 16px; text-align: left;
    font-family: var(--mono); font-size: 10px; color: var(--text3);
    letter-spacing: 0.1em; text-transform: uppercase;
    border-bottom: 1px solid var(--border); background: var(--surface2);
    white-space: nowrap;
  }
  .leads-table td {
    padding: 10px 16px; font-size: 13px; color: var(--text2);
    border-bottom: 1px solid var(--border);
  }
  .leads-table tr:last-child td { border-bottom: none; }
  .leads-table tr.tr-expandible:hover td { background: rgba(255,255,255,.02); color: var(--text); }
  .leads-table tr.fila-detalle td { padding: 0 !important; border: none !important; }
  .leads-table tr.fila-detalle:last-child td { border-bottom: 2px solid var(--accent) !important; }
  .leads-table tr.abierto td { border-bottom: none !important; }
  .td-nombre { color: var(--text) !important; font-weight: 500; }
  .td-mono { font-family: var(--mono); font-size: 11px; }

  /* ESTADO BADGE */
  .badge {
    display: inline-block; padding: 3px 9px; border-radius: 99px;
    font-family: var(--mono); font-size: 10px; font-weight: 600;
    letter-spacing: 0.05em; white-space: nowrap;
  }
  .badge-pendiente { background: rgba(245,166,35,.12); color: var(--accent); }
  .badge-completada { background: rgba(0,229,160,.12); color: var(--green); }
  .badge-solicitud { background: rgba(77,159,255,.12); color: var(--blue); }

  /* DELETE BTN */
  .btn-delete {
    background: none; border: 1px solid transparent; color: var(--text3);
    border-radius: 6px; padding: 4px 8px; cursor: pointer;
    font-size: 12px; transition: all .15s;
  }
  .btn-delete:hover { border-color: rgba(255,91,91,.4); color: var(--red); }

  /* PAGINATION */
  .pagination {
    display: flex; align-items: center; justify-content: space-between;
    padding: 12px 16px; border-top: 1px solid var(--border);
    font-size: 12px; color: var(--text3);
  }
  .page-btns { display: flex; gap: 4px; }
  .page-btn {
    background: var(--surface2); border: 1px solid var(--border);
    color: var(--text2); border-radius: 6px; padding: 4px 10px;
    font-size: 12px; cursor: pointer; transition: all .15s;
  }
  .page-btn:hover { border-color: var(--accent); color: var(--accent); }
  .page-btn.active { background: var(--accent); color: #000; border-color: var(--accent); font-weight: 700; }
  .page-btn:disabled { opacity: 0.3; cursor: not-allowed; }

  /* TARIFAS */
  .tarifas-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
  .upload-zone {
    background: var(--surface); border: 2px dashed var(--border2);
    border-radius: var(--radius); padding: 36px 24px;
    text-align: center; cursor: pointer; transition: all .2s;
  }
  .upload-zone:hover, .upload-zone.drag { border-color: var(--accent); background: rgba(245,166,35,.04); }
  .upload-icon { font-size: 36px; margin-bottom: 12px; }
  .upload-title { font-family: var(--display); font-size: 16px; font-weight: 700; margin-bottom: 6px; }
  .upload-sub { font-size: 13px; color: var(--text2); }
  .btn-upload {
    margin-top: 16px; background: var(--accent); color: #000;
    border: none; border-radius: 8px; padding: 10px 20px;
    font-family: var(--display); font-size: 13px; font-weight: 700;
    cursor: pointer; transition: opacity .15s;
  }
  .btn-upload:hover { opacity: .85; }
  .tarifa-list-card {
    background: var(--surface); border: 1px solid var(--border);
    border-radius: var(--radius); overflow: hidden;
  }
  .tarifa-list-header {
    padding: 14px 18px; border-bottom: 1px solid var(--border);
    font-family: var(--mono); font-size: 11px; color: var(--text3);
    letter-spacing: 0.1em; text-transform: uppercase;
    display: flex; justify-content: space-between; align-items: center;
  }
  .tarifa-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 11px 18px; border-bottom: 1px solid var(--border); font-size: 13px;
  }
  .tarifa-row:last-child { border-bottom: none; }
  .tarifa-comercial { font-weight: 600; color: var(--text); }
  .tarifa-nombre { color: var(--text2); font-size: 12px; margin-top: 2px; }
  .tarifa-precio { font-family: var(--mono); font-size: 12px; color: var(--accent); }

  /* LOGIN */
  .login-wrapper {
    min-height: 100vh; display: flex; align-items: center; justify-content: center;
    background: var(--bg);
    background-image: radial-gradient(ellipse at 20% 50%, rgba(245,166,35,.06) 0%, transparent 60%),
                      radial-gradient(ellipse at 80% 20%, rgba(0,229,160,.04) 0%, transparent 50%);
  }
  .login-card {
    background: var(--surface); border: 1px solid var(--border2);
    border-radius: 16px; padding: 44px 40px; width: 100%; max-width: 420px;
    box-shadow: 0 24px 80px rgba(0,0,0,.5);
  }
  .login-logo { font-family: var(--display); font-size: 22px; font-weight: 800; margin-bottom: 6px; }
  .login-logo span { color: var(--accent); }
  .login-sub { font-family: var(--mono); font-size: 10px; color: var(--text3); letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 32px; }
  .login-field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; }
  .login-label { font-family: var(--mono); font-size: 10px; color: var(--accent); letter-spacing: 0.1em; text-transform: uppercase; }
  .login-input {
    background: var(--surface2); border: 1px solid var(--border2);
    border-radius: 10px; padding: 12px 16px; color: var(--text);
    font-family: var(--body); font-size: 14px; outline: none;
    transition: border-color .2s;
  }
  .login-input:focus { border-color: var(--accent); }
  .login-btn {
    width: 100%; padding: 13px; background: var(--accent); color: #000;
    border: none; border-radius: 10px; font-family: var(--display);
    font-size: 15px; font-weight: 800; cursor: pointer;
    transition: opacity .15s; margin-top: 8px;
  }
  .login-btn:hover { opacity: .85; }
  .login-error {
    background: rgba(255,91,91,.08); border: 1px solid rgba(255,91,91,.3);
    border-radius: 8px; padding: 10px 14px; color: var(--red);
    font-size: 13px; margin-bottom: 16px;
  }

  /* EMPTY STATE */
  .empty-state {
    text-align: center; padding: 60px 20px; color: var(--text3);
  }
  .empty-icon { font-size: 40px; margin-bottom: 12px; }
  .empty-text { font-size: 14px; }

  /* CONFIRM MODAL */
  .modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,.7);
    display: flex; align-items: center; justify-content: center; z-index: 100;
    backdrop-filter: blur(4px);
  }
  .modal-box {
    background: var(--surface); border: 1px solid var(--border2);
    border-radius: 14px; padding: 32px; max-width: 360px; width: 90%;
    text-align: center;
  }
  .modal-title { font-family: var(--display); font-size: 18px; font-weight: 700; margin-bottom: 10px; }
  .modal-sub { color: var(--text2); font-size: 13px; line-height: 1.6; margin-bottom: 24px; }
  .modal-btns { display: flex; gap: 10px; }
  .btn-cancel {
    flex: 1; padding: 10px; background: none; border: 1px solid var(--border2);
    color: var(--text2); border-radius: 8px; cursor: pointer; font-size: 13px;
    transition: all .15s;
  }
  .btn-cancel:hover { border-color: var(--text2); color: var(--text); }
  .btn-confirm-delete {
    flex: 1; padding: 10px; background: rgba(255,91,91,.15);
    border: 1px solid rgba(255,91,91,.4); color: var(--red);
    border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 600;
    transition: all .15s;
  }
  .btn-confirm-delete:hover { background: rgba(255,91,91,.25); }

  /* VERSIONES EXCEL */
  .version-list { display: flex; flex-direction: column; gap: 8px; margin-top: 16px; }
  .version-item {
    display: flex; align-items: center; justify-content: space-between;
    background: var(--surface2); border: 1px solid var(--border);
    border-radius: 10px; padding: 12px 16px; transition: border-color .2s;
  }
  .version-item.activa { border-color: rgba(0,229,160,.4); background: rgba(0,229,160,.04); }
  .version-info { display: flex; flex-direction: column; gap: 3px; }
  .version-nombre { font-size: 13px; font-weight: 600; color: var(--text); }
  .version-fecha { font-family: var(--mono); font-size: 10px; color: var(--text3); }
  .version-actions { display: flex; gap: 6px; align-items: center; }
  .btn-version {
    padding: 5px 10px; border-radius: 6px; font-size: 11px; font-family: var(--mono);
    cursor: pointer; transition: all .15s; border: 1px solid var(--border2);
    background: none; color: var(--text2);
  }
  .btn-version:hover { border-color: var(--accent); color: var(--accent); }
  .btn-version.activar { border-color: rgba(0,229,160,.4); color: var(--green); }
  .btn-version.activar:hover { background: rgba(0,229,160,.1); }
  .btn-version.eliminar { border-color: rgba(255,91,91,.3); color: var(--red); }
  .btn-version.eliminar:hover { background: rgba(255,91,91,.1); }

  /* TOGGLE ACTIVA/DESACTIVADA */
  .toggle-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 4px 12px; border-radius: 99px; cursor: pointer;
    font-family: var(--mono); font-size: 10px; font-weight: 700;
    letter-spacing: 0.05em; transition: all .2s; border: 1px solid;
    white-space: nowrap;
  }
  .toggle-btn.on {
    background: rgba(0,229,160,.12); border-color: rgba(0,229,160,.4); color: var(--green);
  }
  .toggle-btn.on:hover { background: rgba(0,229,160,.22); }
  .toggle-btn.off {
    background: rgba(255,91,91,.08); border-color: rgba(255,91,91,.3); color: var(--red);
  }
  .toggle-btn.off:hover { background: rgba(255,91,91,.16); }
  .toggle-dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: currentColor;
  }
  .badge-activa {
    font-family: var(--mono); font-size: 9px; font-weight: 700;
    background: rgba(0,229,160,.15); color: var(--green);
    border: 1px solid rgba(0,229,160,.3); border-radius: 99px; padding: 2px 8px;
    letter-spacing: 0.08em;
  }

  /* DESPLEGABLE TARIFA */
  .tarifa-row { cursor: pointer; }
  .tarifa-row:hover { background: rgba(255,255,255,.02); }
  .tarifa-chevron { font-size: 10px; color: var(--text3); transition: transform .2s; }
  .tarifa-chevron.open { transform: rotate(180deg); }
  .tarifa-detalle {
    padding: 12px 18px 14px; border-bottom: 1px solid var(--border);
    background: var(--surface2);
    display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 10px;
  }
  .detalle-item { display: flex; flex-direction: column; gap: 3px; }
  .detalle-label { font-family: var(--mono); font-size: 9px; color: var(--text3); letter-spacing: 0.08em; text-transform: uppercase; }
  .detalle-valor { font-family: var(--mono); font-size: 12px; color: var(--accent); font-weight: 600; }
  .detalle-valor.neutral { color: var(--text2); }

  /* NAVBAR MÓVIL */
  .mobile-nav {
    display: none;
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    padding: 8px 12px;
    gap: 6px;
  }
  .mobile-nav-btn {
    flex: 1; padding: 9px 12px; border-radius: 8px; border: none;
    background: none; color: var(--text2); font-family: var(--mono);
    font-size: 11px; cursor: pointer; transition: all .15s; text-align: center;
  }
  .mobile-nav-btn.active { background: rgba(245,166,35,.1); color: var(--accent); font-weight: 700; }

  /* FILA EXPANDIDA */
  .lead-expandido {
    background: var(--surface2);
    padding: 20px 24px;
    border-bottom: 2px solid var(--accent);
  }
  .lead-expandido-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 16px 20px;
  }
  .lead-campo { display: flex; flex-direction: column; gap: 4px; }
  .lead-campo-label {
    font-family: var(--mono); font-size: 9px; color: var(--text3);
    letter-spacing: 0.1em; text-transform: uppercase;
  }
  .lead-campo-valor { font-size: 13px; color: var(--text); font-weight: 500; }
  .lead-campo-valor.accent { color: var(--accent); }
  .lead-campo-valor.green { color: var(--green); }
  .lead-campo-valor.red { color: var(--red); }
  .tr-expandible { cursor: pointer; }
  .tr-expandible:hover td { background: rgba(245,166,35,.03) !important; }
  .tr-expandible.abierto td { background: rgba(245,166,35,.05) !important; border-bottom: none; }
  .td-chevron { color: var(--text3); font-size: 10px; transition: transform .2s; display: inline-block; }
  .td-chevron.open { transform: rotate(180deg); }


  @media (max-width: 768px) {
    .sidebar { display: none; }
    .mobile-nav { display: flex; }
    .tarifas-grid { grid-template-columns: 1fr; }
    .stats-grid { grid-template-columns: 1fr 1fr; }
    .panel-content { padding: 16px; }
    .panel-topbar { padding: 12px 16px; }
    .topbar-badge { display: none; }
    .leads-desktop-header { display: none; }
    .lead-fila-desktop { display: none !important; }
    .lead-card-mobile { display: flex !important; }
  }
  .lead-card-mobile { display: none; }
`;

// ── DATOS DE EJEMPLO ───────────────────────────────────────────────────────────
const POR_PAGINA = 5;

// ── BADGE ESTADO ──────────────────────────────────────────────────────────────
function BadgeEstado({ estado }) {
  const map = {
    pendiente_factura:       { label: "Pendiente",   cls: "badge-pendiente" },
    comparativa_completada:  { label: "Completada",  cls: "badge-completada" },
    solicitud_asesoramiento: { label: "Solicitud",   cls: "badge-solicitud" },
  };
  const { label, cls } = map[estado] || { label: estado, cls: "badge-pendiente" };
  return <span className={`badge ${cls}`}>{label}</span>;
}

// ── LOGIN ──────────────────────────────────────────────────────────────────────
function Login({ onLogin }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(false);

  const handleLogin = async () => {
    if (!form.email || !form.password) { setError("Rellena todos los campos."); return; }
    setCargando(true);
    setError("");
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: form.email,
        password: form.password,
      });
      if (error) throw error;
      onLogin({ email: data.user.email });
    } catch (e) {
      setError("Email o contraseña incorrectos.");
      setCargando(false);
    }
  };

  return (
    <div className="login-wrapper" style={{
      background: "var(--bg)",
      backgroundImage: `url(${fondoLogin})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
    }}>
      <div className="login-card">
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <img src={logoLogin} alt="Nerón Energía" style={{ width: 160, height: 160, objectFit: "contain" }} />
        </div>
        <div className="login-sub" style={{ textAlign: "center", marginBottom: 32 }}>Panel de administración</div>
        {error && <div className="login-error">⚠ {error}</div>}
        <div className="login-field">
          <label className="login-label">Email</label>
          <input className="login-input" type="email" placeholder="admin@neron.es"
            value={form.email} onChange={e => { setForm({...form, email: e.target.value}); setError(""); }}
            onKeyDown={e => e.key === "Enter" && handleLogin()} />
        </div>
        <div className="login-field">
          <label className="login-label">Contraseña</label>
          <input className="login-input" type="password" placeholder="••••••••"
            value={form.password} onChange={e => { setForm({...form, password: e.target.value}); setError(""); }}
            onKeyDown={e => e.key === "Enter" && handleLogin()} />
        </div>
        <button className="login-btn" onClick={handleLogin} disabled={cargando}>
          {cargando ? "Entrando..." : "Acceder al panel →"}
        </button>
      </div>
    </div>
  );
}

// ── SECCIÓN LEADS ─────────────────────────────────────────────────────────────
function SeccionLeads() {
  const API = process.env.REACT_APP_API_URL || "http://localhost:8000";
  const API_KEY = process.env.REACT_APP_API_KEY || "";

  const [leads, setLeads] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [errorLeads, setErrorLeads] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [pagina, setPagina] = useState(1);
  const [modalBorrar, setModalBorrar] = useState(null);
  const [expandido, setExpandido] = useState(null);

  const toggleExpandir = (id) => setExpandido(prev => prev === id ? null : id);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const cargarLeads = async () => {
      try {
        const r = await fetch(`${API}/api/leads`, {
          headers: { "X-API-Key": API_KEY }
        });
        if (!r.ok) throw new Error("Error al cargar leads");
        const data = await r.json();
        setLeads(data.leads || []);
      } catch (e) {
        setErrorLeads("No se pudieron cargar los leads. ¿Está el backend arrancado?");
      } finally {
        setCargando(false);
      }
    };
    cargarLeads();
  }, []);

  const filtrados = useMemo(() => {
    return leads.filter(l => {
      const matchBusqueda = !busqueda ||
        l.nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
        l.email?.toLowerCase().includes(busqueda.toLowerCase()) ||
        l.telefono?.includes(busqueda) ||
        l.cups?.toLowerCase().includes(busqueda.toLowerCase());
      const matchEstado = filtroEstado === "todos" || l.estado === filtroEstado;
      return matchBusqueda && matchEstado;
    });
  }, [leads, busqueda, filtroEstado]);

  const totalPaginas = Math.ceil(filtrados.length / POR_PAGINA);
  const paginados = filtrados.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

  const confirmarBorrar = async (id) => {
    try {
      await fetch(`${API}/api/leads/${id}`, {
        method: "DELETE",
        headers: { "X-API-Key": API_KEY }
      });
    } catch (e) {
      console.error("Error al borrar lead:", e);
    }
    setLeads(leads.filter(l => l.id !== id));
    setModalBorrar(null);
  };

  const exportarLeadIndividual = async (lead) => {
    try {
      const r = await fetch(`${API}/api/leads/${lead.id}/export`, {
        headers: { "X-API-Key": API_KEY }
      });
      if (!r.ok) throw new Error("Error al exportar");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const nombre = `lead_${(lead.nombre || 'unknown').replace(/ /g,'_')}.csv`;
      const a = document.createElement("a"); a.href = url; a.download = nombre; a.click();
    } catch (e) {
      console.error("Error al exportar lead:", e);
    }
  };

  const exportarCSV = async () => {
    try {
      const r = await fetch(`${API}/api/leads/export`, {
        headers: { "X-API-Key": API_KEY }
      });
      if (!r.ok) throw new Error("Error al exportar");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "leads.csv"; a.click();
    } catch (e) {
      console.error("Error al exportar CSV:", e);
    }
  };

  const totalAhorro = leads.filter(l => l.ahorro_estimado).reduce((s, l) => s + l.ahorro_estimado, 0);
  const completadas = leads.filter(l => l.estado === "comparativa_completada").length;
  const solicitudes = leads.filter(l => l.estado === "solicitud_asesoramiento").length;

  return (
    <div>
      {/* STATS */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total leads</div>
          <div className="stat-value accent">{leads.length}</div>
          <div className="stat-sub">registrados</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Comparativas</div>
          <div className="stat-value">{completadas}</div>
          <div className="stat-sub">completadas</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Solicitudes</div>
          <div className="stat-value green">{solicitudes}</div>
          <div className="stat-sub">de asesoramiento</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Ahorro total</div>
          <div className="stat-value accent">{totalAhorro.toFixed(0)}€</div>
          <div className="stat-sub">estimado a clientes</div>
        </div>
      </div>

      {errorLeads && (
        <div style={{
          padding: "14px 18px", background: "rgba(255,91,91,.08)",
          border: "1px solid rgba(255,91,91,.3)", borderRadius: 10,
          color: "var(--red)", fontSize: 13, marginBottom: 16, lineHeight: 1.6
        }}>
          ⚠ <strong>No se pudieron cargar los leads</strong><br />
          <span style={{ color: "var(--text2)", fontSize: 12 }}>
            Asegúrate de que el backend está arrancado con{" "}
            <code style={{ fontFamily: "var(--mono)", background: "rgba(255,255,255,.06)", padding: "1px 6px", borderRadius: 4 }}>
              py -3.11 -m uvicorn main:app --reload
            </code>
          </span>
        </div>
      )}

      {cargando && (
        <div style={{ textAlign: "center", padding: "40px", color: "var(--text3)", fontFamily: "var(--mono)", fontSize: 12 }}>
          Cargando leads...
        </div>
      )}

      {/* LEYENDA DE ESTADOS */}
      <div style={{
        display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap"
      }}>
        {[
          { cls: "badge-pendiente", label: "Pendiente", desc: "Rellenó el formulario pero no analizó la factura" },
          { cls: "badge-completada", label: "Completada", desc: "Vio el ranking de tarifas pero no solicitó asesoramiento" },
          { cls: "badge-solicitud",  label: "Solicitud",  desc: "Solicitó asesoramiento y está a la espera de ser contactado" },
        ].map(({ cls, label, desc }) => (
          <div key={label} style={{
            display: "flex", alignItems: "center", gap: 8,
            background: "var(--surface)", border: "1px solid var(--border)",
            borderRadius: 8, padding: "7px 12px",
          }}>
            <span className={`badge ${cls}`}>{label}</span>
            <span style={{ fontSize: 11, color: "var(--text3)" }}>{desc}</span>
          </div>
        ))}
      </div>

      {/* TOOLBAR */}
      <div className="toolbar">
        <input className="search-box" placeholder="🔍  Buscar por nombre, teléfono, email o CUPS..."
          value={busqueda} onChange={e => { setBusqueda(e.target.value); setPagina(1); }} />
        <select className="filter-select" value={filtroEstado}
          onChange={e => { setFiltroEstado(e.target.value); setPagina(1); }}>
          <option value="todos">Todos los estados</option>
          <option value="pendiente_factura">Pendiente</option>
          <option value="comparativa_completada">Completada</option>
          <option value="solicitud_asesoramiento">Solicitud</option>
        </select>
        <button className="btn-export" onClick={exportarCSV}>⬇ Exportar CSV</button>
      </div>

      {/* TABLA */}
      <div className="table-wrapper">
        {/* CABECERA — solo desktop */}
        <div className="leads-desktop-header" style={{
          display: "grid",
          gridTemplateColumns: "1.8fr 1.6fr 1fr 0.7fr 1fr 0.8fr 0.8fr 1.2fr 1.1fr 80px",
          padding: "10px 16px", background: "var(--surface2)",
          borderBottom: "1px solid var(--border)",
          fontFamily: "var(--mono)", fontSize: 10, color: "var(--text3)",
          letterSpacing: "0.1em", textTransform: "uppercase",
        }}>
          <div>Nombre</div><div>Contacto</div><div>Estado</div>
          <div>Potencia</div><div>Consumo</div><div>Importe</div>
          <div>Ahorro</div><div>Mejor tarifa</div><div>Fecha</div><div></div>
        </div>

        {/* FILAS */}
        {paginados.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <div className="empty-text">No se encontraron leads</div>
          </div>
        ) : paginados.map(lead => (
          <div key={lead.id} style={{ borderBottom: "1px solid var(--border)" }}>
            {/* TARJETA MÓVIL */}
            <div className="lead-card-mobile" style={{
              padding: "14px 16px", flexDirection: "column", gap: 8,
              cursor: "pointer", background: expandido === lead.id ? "rgba(245,166,35,.04)" : "transparent",
            }} onClick={() => toggleExpandir(lead.id)}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontWeight: 600, color: "var(--text)", fontSize: 14, display: "flex", gap: 6, alignItems: "center" }}>
                  <span className={`td-chevron ${expandido === lead.id ? "open" : ""}`}>▼</span>
                  {lead.nombre}
                </div>
                <BadgeEstado estado={lead.estado} />
              </div>
              <div style={{ fontSize: 12, color: "var(--text2)" }}>{lead.telefono} · {lead.email}</div>
              <div style={{ display: "flex", gap: 12, fontSize: 12 }}>
                {lead.importe_actual && <span style={{ color: "var(--text2)" }}>💶 {lead.importe_actual}€</span>}
                {lead.ahorro_estimado != null && (
                  <span style={{ color: lead.ahorro_estimado < 0 ? "var(--red)" : "var(--green)", fontWeight: 600 }}>
                    {lead.ahorro_estimado >= 0 ? "↓" : "↑"} {Math.abs(lead.ahorro_estimado)}€
                  </span>
                )}
                {lead.mejor_tarifa && <span style={{ color: "var(--text3)" }}>{lead.mejor_tarifa}</span>}
              </div>
              <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }} onClick={e => e.stopPropagation()}>
                <button className="btn-delete" onClick={() => exportarLeadIndividual(lead)}>⬇</button>
                <button className="btn-delete" onClick={() => setModalBorrar(lead)}>🗑</button>
              </div>
            </div>

            {/* FILA PRINCIPAL — solo desktop */}
            <div className="lead-fila-desktop"
              onClick={() => toggleExpandir(lead.id)}
              style={{
                display: "grid",
                gridTemplateColumns: "1.8fr 1.6fr 1fr 0.7fr 1fr 0.8fr 0.8fr 1.2fr 1.1fr 80px",
                padding: "10px 16px", cursor: "pointer", alignItems: "center",
                background: expandido === lead.id ? "rgba(245,166,35,.04)" : "transparent",
                transition: "background .15s",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--text)", fontWeight: 500, fontSize: 13 }}>
                <span className={`td-chevron ${expandido === lead.id ? "open" : ""}`}>▼</span>
                {lead.nombre}
              </div>
              <div>
                <div className="td-mono">{lead.telefono}</div>
                <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>{lead.email}</div>
              </div>
              <div><BadgeEstado estado={lead.estado} /></div>
              <div className="td-mono">{lead.potencia_kw ? `${lead.potencia_kw} kW` : "—"}</div>
              <div className="td-mono">{lead.consumo_kwh ? `${lead.consumo_kwh} kWh` : "—"}</div>
              <div className="td-mono">{lead.importe_actual ? `${lead.importe_actual}€` : "—"}</div>
              <div className="td-mono" style={{
                color: lead.ahorro_estimado == null ? "var(--text3)"
                  : lead.ahorro_estimado < 0 ? "var(--red)"
                  : "var(--green)",
                fontWeight: lead.ahorro_estimado != null ? 600 : 400
              }}>
                {lead.ahorro_estimado != null ? `${lead.ahorro_estimado}€` : "—"}
              </div>
              <div style={{ fontSize: 12, color: "var(--text2)" }}>{lead.mejor_tarifa || "—"}</div>
              <div className="td-mono" style={{ fontSize: 11 }}>{lead.fecha?.slice(0,16).replace("T"," ")}</div>
              <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: 4 }}>
                <button className="btn-delete" title="Descargar CSV" onClick={() => exportarLeadIndividual(lead)}>⬇</button>
                <button className="btn-delete" title="Eliminar" onClick={() => setModalBorrar(lead)}>🗑</button>
              </div>
            </div>

            {/* DETALLE EXPANDIDO */}
            {expandido === lead.id && (
              <div style={{
                background: "var(--surface2)", padding: "20px 24px",
                borderTop: "1px solid var(--border)",
                borderBottom: "2px solid var(--accent)",
              }}>
                <div className="lead-expandido-grid">
                  <div className="lead-campo"><div className="lead-campo-label">ID</div><div className="lead-campo-valor">{lead.id}</div></div>
                  <div className="lead-campo"><div className="lead-campo-label">Nombre</div><div className="lead-campo-valor">{lead.nombre || "—"}</div></div>
                  <div className="lead-campo"><div className="lead-campo-label">Teléfono</div><div className="lead-campo-valor">{lead.telefono || "—"}</div></div>
                  <div className="lead-campo"><div className="lead-campo-label">Email</div><div className="lead-campo-valor">{lead.email || "—"}</div></div>
                  <div className="lead-campo"><div className="lead-campo-label">Estado</div><div className="lead-campo-valor"><BadgeEstado estado={lead.estado} /></div></div>
                  <div className="lead-campo"><div className="lead-campo-label">Origen</div><div className="lead-campo-valor">{lead.origen || "—"}</div></div>
                  <div className="lead-campo"><div className="lead-campo-label">Consentimiento</div><div className="lead-campo-valor">{lead.consentimiento ? "✅ Sí" : "❌ No"}</div></div>
                  <div className="lead-campo"><div className="lead-campo-label">Fecha</div><div className="lead-campo-valor">{lead.fecha?.slice(0,16).replace("T"," ") || "—"}</div></div>
                  <div className="lead-campo"><div className="lead-campo-label">CUPS</div><div className="lead-campo-valor" style={{ fontSize: 11, fontFamily: "var(--mono)" }}>{lead.cups || "—"}</div></div>
                  <div className="lead-campo"><div className="lead-campo-label">Potencia</div><div className="lead-campo-valor">{lead.potencia_kw ? `${lead.potencia_kw} kW` : "—"}</div></div>
                  <div className="lead-campo"><div className="lead-campo-label">Días facturados</div><div className="lead-campo-valor">{lead.dias_facturados || "—"}</div></div>
                  <div className="lead-campo"><div className="lead-campo-label">Consumo total</div><div className="lead-campo-valor">{lead.consumo_kwh ? `${lead.consumo_kwh} kWh` : "—"}</div></div>
                  <div className="lead-campo"><div className="lead-campo-label">P1 Punta</div><div className="lead-campo-valor">{lead.consumo_p1 ? `${lead.consumo_p1} kWh` : "—"}</div></div>
                  <div className="lead-campo"><div className="lead-campo-label">P2 Llano</div><div className="lead-campo-valor">{lead.consumo_p2 ? `${lead.consumo_p2} kWh` : "—"}</div></div>
                  <div className="lead-campo"><div className="lead-campo-label">P3 Valle</div><div className="lead-campo-valor">{lead.consumo_p3 ? `${lead.consumo_p3} kWh` : "—"}</div></div>
                  <div className="lead-campo"><div className="lead-campo-label">Importe actual</div><div className="lead-campo-valor accent">{lead.importe_actual ? `${lead.importe_actual}€` : "—"}</div></div>
                  <div className="lead-campo"><div className="lead-campo-label">Ahorro estimado</div><div className={`lead-campo-valor ${lead.ahorro_estimado == null ? "" : lead.ahorro_estimado < 0 ? "red" : "green"}`}>{lead.ahorro_estimado != null ? `${lead.ahorro_estimado}€` : "—"}</div></div>
                  <div className="lead-campo"><div className="lead-campo-label">Mejor tarifa</div><div className="lead-campo-valor">{lead.mejor_tarifa || "—"}</div></div>
                  {lead.agente && <div className="lead-campo"><div className="lead-campo-label">Agente</div><div className="lead-campo-valor">{lead.agente}</div></div>}
                  {lead.notas && <div className="lead-campo" style={{ gridColumn: "1 / -1" }}><div className="lead-campo-label">Notas</div><div className="lead-campo-valor">{lead.notas}</div></div>}
                </div>
              </div>
            )}
          </div>
        ))}

        {/* PAGINACIÓN */}
        {totalPaginas > 1 && (
          <div className="pagination" style={{ borderTop: "1px solid var(--border)" }}>
            <span>Mostrando {(pagina-1)*POR_PAGINA+1}–{Math.min(pagina*POR_PAGINA, filtrados.length)} de {filtrados.length}</span>
            <div className="page-btns">
              <button className="page-btn" onClick={() => setPagina(p => p-1)} disabled={pagina === 1}>←</button>
              {Array.from({ length: totalPaginas }, (_, i) => (
                <button key={i+1} className={`page-btn ${pagina === i+1 ? "active" : ""}`}
                  onClick={() => setPagina(i+1)}>{i+1}</button>
              ))}
              <button className="page-btn" onClick={() => setPagina(p => p+1)} disabled={pagina === totalPaginas}>→</button>
            </div>
          </div>
        )}
      </div>

      {/* MODAL BORRAR */}
      {modalBorrar && (
        <div className="modal-overlay" onClick={() => setModalBorrar(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title">¿Borrar este lead?</div>
            <div className="modal-sub">
              Vas a eliminar los datos de <strong>{modalBorrar.nombre}</strong>.<br />
              Esta acción es irreversible (RGPD).
            </div>
            <div className="modal-btns">
              <button className="btn-cancel" onClick={() => setModalBorrar(null)}>Cancelar</button>
              <button className="btn-confirm-delete" onClick={() => confirmarBorrar(modalBorrar.id)}>Borrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── SECCIÓN TARIFAS ───────────────────────────────────────────────────────────
function SeccionTarifas() {
  const API = process.env.REACT_APP_API_URL || "http://localhost:8000";
  const API_KEY = process.env.REACT_APP_API_KEY || "";

  const [drag, setDrag] = useState(false);
  const [archivo, setArchivo] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [versiones, setVersiones] = useState([]);
  const [abiertos, setAbiertos] = useState({});
  const [tarifas, setTarifas] = useState([]);
  const [cargandoTarifas, setCargandoTarifas] = useState(true);
  const [errorTarifas, setErrorTarifas] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        // Cargar tarifas
        const r = await fetch(`${API}/api/tarifas`, {
          headers: { "X-API-Key": API_KEY }
        });
        if (!r.ok) throw new Error("Error al cargar tarifas");
        const data = await r.json();
        const tarifasConEstado = (data.tarifas || []).map(t => ({
          ...t,
          _activa: String(t.activa).toUpperCase() === 'SI'
        }));
        setTarifas(tarifasConEstado);

        // Cargar versiones
        const r2 = await fetch(`${API}/api/tarifas/versiones`, {
          headers: { "X-API-Key": API_KEY }
        });
        if (r2.ok) {
          const data2 = await r2.json();
          setVersiones(data2.versiones || []);
        }
      } catch (e) {
        setErrorTarifas("No se pudieron cargar las tarifas. ¿Está el backend arrancado?");
      } finally {
        setCargandoTarifas(false);
      }
    };
    cargarDatos();
  }, []);

  const handleFile = (f) => {
    if (f && f.name.endsWith(".xlsx")) { setArchivo(f); setMensaje(""); }
    else setMensaje("Solo se admiten archivos .xlsx");
  };

  const handleSubir = async () => {
    if (!archivo) return;
    setSubiendo(true);
    setMensaje("");
    try {
      const formData = new FormData();
      formData.append("archivo", archivo);
      const r = await fetch(`${API}/api/tarifas/upload`, {
        method: "POST",
        headers: { "X-API-Key": API_KEY },
        body: formData
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || "Error al subir el archivo");
      // Recargar tarifas
      const r2 = await fetch(`${API}/api/tarifas`, { headers: { "X-API-Key": API_KEY } });
      const data2 = await r2.json();
      const tarifasConEstado = (data2.tarifas || []).map(t => ({
        ...t,
        _activa: String(t.activa).toUpperCase() === 'SI'
      }));
      setTarifas(tarifasConEstado);
      const nueva = {
        id: versiones.length + 1,
        nombre: archivo.name,
        fecha: new Date().toISOString().slice(0, 16).replace("T", " "),
        activa: true,
        tarifas: data2.tarifas?.length || 0,
      };
      setVersiones([nueva, ...versiones.map(v => ({ ...v, activa: false }))]);
      setMensaje(`✅ ${data.mensaje || "Tarifas actualizadas correctamente."}`);
    } catch (e) {
      setMensaje(`❌ Error al subir: ${e.message}. Comprueba que el backend está arrancado y que el archivo es un .xlsx válido.`);
    } finally {
      setSubiendo(false);
    }
  };

  const eliminarVersion = async (v) => {
    try {
      const r = await fetch(`${API}/api/tarifas/versiones/${v.id}`, {
        method: "DELETE",
        headers: { "X-API-Key": API_KEY }
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || "Error al eliminar");
      setVersiones(prev => prev.filter(ver => ver.id !== v.id));
    } catch (e) {
      setMensaje(`❌ ${e.message}`);
    }
  };

  const activarVersion = async (id) => {
    try {
      const r = await fetch(`${API}/api/tarifas/activar/${id}`, {
        method: "POST",
        headers: { "X-API-Key": API_KEY }
      });
      if (!r.ok) throw new Error("Error al activar");
      setVersiones(versiones.map(v => ({ ...v, activa: v.id === id })));
      // Recargar tarifas activas
      const r2 = await fetch(`${API}/api/tarifas`, { headers: { "X-API-Key": API_KEY } });
      const data = await r2.json();
      setTarifas((data.tarifas || []).map(t => ({ ...t, _activa: String(t.activa).toUpperCase() === 'SI' })));
    } catch (e) {
      console.error("Error al activar versión:", e);
    }
  };

  const descargarVersion = async (v) => {
    try {
      const r = await fetch(`${API}/api/tarifas/descargar/${v.id}`, {
        headers: { "X-API-Key": API_KEY }
      });
      if (!r.ok) throw new Error("Error al descargar");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = v.nombre; a.click();
    } catch (e) {
      console.error("Error al descargar:", e);
    }
  };

  const toggleDetalle = (i) => {
    setAbiertos(prev => ({ ...prev, [i]: !prev[i] }));
  };

  const toggleTodas = async (estado) => {
    const prevTarifas = tarifas;
    setTarifas(prev => prev.map(t => ({ ...t, _activa: estado === "SI" })));
    try {
      const formData = new FormData();
      formData.append("activa", estado);
      const r = await fetch(`${API}/api/tarifas/toggle-todas`, {
        method: "POST",
        headers: { "X-API-Key": API_KEY },
        body: formData
      });
      if (!r.ok) {
        setTarifas(prevTarifas);
        setMensaje("❌ Error al actualizar todas las tarifas.");
      }
    } catch (e) {
      setTarifas(prevTarifas);
      setMensaje("❌ Error al conectar con el backend.");
    }
  };

  const toggleActiva = async (i) => {
    const tarifa = tarifas[i];
    const nuevoEstado = !tarifa._activa;
    // Actualizar UI inmediatamente
    setTarifas(prev => prev.map((t, j) => j === i ? { ...t, _activa: nuevoEstado } : t));
    // Guardar en backend
    try {
      const formData = new FormData();
      formData.append("nombre_tarifa", String(tarifa.nombre_tarifa));
      formData.append("comercializadora", String(tarifa.comercializadora));
      formData.append("activa", nuevoEstado ? "SI" : "NO");
      const r = await fetch(`${API}/api/tarifas/toggle`, {
        method: "POST",
        headers: { "X-API-Key": API_KEY },
        body: formData
      });
      if (!r.ok) {
        // Revertir si falla
        setTarifas(prev => prev.map((t, j) => j === i ? { ...t, _activa: tarifa._activa } : t));
        setMensaje("❌ Error al guardar el cambio.");
      }
    } catch (e) {
      setTarifas(prev => prev.map((t, j) => j === i ? { ...t, _activa: tarifa._activa } : t));
      setMensaje("❌ Error al conectar con el backend.");
    }
  };

  return (
    <div>
      <div className="tarifas-grid">

        {/* COLUMNA IZQUIERDA — subida + versiones */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* ZONA DE SUBIDA */}
          <div
            className={`upload-zone ${drag ? "drag" : ""}`}
            onDragOver={e => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={e => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
            onClick={() => document.getElementById("excel-input").click()}
          >
            <div className="upload-icon">📊</div>
            <div className="upload-title">{archivo ? archivo.name : "Subir nuevo Excel de tarifas"}</div>
            <div className="upload-sub">{archivo ? "Listo para subir" : "Arrastra el .xlsx aquí o haz clic"}</div>
            <input id="excel-input" type="file" accept=".xlsx" style={{ display: "none" }}
              onChange={e => handleFile(e.target.files[0])} />
          </div>

          {mensaje && (
            <div style={{
              padding: "10px 14px", borderRadius: 8, fontSize: 13,
              background: mensaje.startsWith("✅") ? "rgba(0,229,160,.08)" : "rgba(255,91,91,.08)",
              border: `1px solid ${mensaje.startsWith("✅") ? "rgba(0,229,160,.3)" : "rgba(255,91,91,.3)"}`,
              color: mensaje.startsWith("✅") ? "var(--green)" : "var(--red)",
            }}>{mensaje}</div>
          )}

          {archivo && (
            <button className="btn-upload" style={{ width: "100%" }}
              onClick={handleSubir} disabled={subiendo}>
              {subiendo ? "Subiendo..." : "⬆ Subir Excel"}
            </button>
          )}

          {/* HISTORIAL DE VERSIONES */}
          <div className="tarifa-list-card">
            <div className="tarifa-list-header">
              <span>Historial de versiones</span>
              <span style={{ color: "var(--accent)" }}>{versiones.length} archivos</span>
            </div>
            <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
              {versiones.map(v => (
                <div key={v.id} className={`version-item ${v.activa ? "activa" : ""}`}>
                  <div className="version-info">
                    <div className="version-nombre">
                      {v.nombre}
                      {v.activa && <span className="badge-activa" style={{ marginLeft: 8 }}>ACTIVA</span>}
                    </div>
                    <div className="version-fecha">{v.fecha?.slice(0,16).replace('T',' ')} · {v.num_tarifas} tarifas</div>
                  </div>
                  <div className="version-actions">
                    <button className="btn-version" onClick={() => descargarVersion(v)}>⬇ Descargar</button>
                    {!v.activa && (
                      <button className="btn-version activar" onClick={() => activarVersion(v.id)}>✓ Activar</button>
                    )}
                    {!v.activa && (
                      <button className="btn-version eliminar" onClick={() => eliminarVersion(v)}>🗑</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* FORMATO */}
          <div style={{
            padding: "12px 16px", background: "var(--surface)",
            border: "1px solid var(--border)", borderRadius: 10, fontSize: 12, color: "var(--text2)", lineHeight: 1.7,
          }}>
            <strong style={{ color: "var(--text)" }}>Formato requerido:</strong> el Excel debe tener las columnas
            <span style={{ fontFamily: "var(--mono)", color: "var(--accent)", fontSize: 11 }}> comercializadora, nombre_tarifa, precio_potencia_p1, precio_potencia_p2,
            precio_energia_p1, precio_energia_p2, precio_energia_p3, descuento_pct, permanencia_meses, enlace_contratacion, activa</span>
          </div>
        </div>

        {/* COLUMNA DERECHA — tarifas activas con desplegable */}
        <div className="tarifa-list-card">
          <div className="tarifa-list-header">
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span>Tarifas del Excel activo</span>
              <span style={{ fontSize: 10, color: "var(--text3)", fontFamily: "var(--mono)" }}>
                Total: {tarifas.length} · Activas: {tarifas.filter(t => t._activa).length} · Inactivas: {tarifas.filter(t => !t._activa).length}
              </span>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button className="btn-version activar" onClick={() => toggleTodas("SI")} style={{ fontSize: 10, padding: "4px 8px" }}>✓ Todas</button>
              <button className="btn-version eliminar" onClick={() => toggleTodas("NO")} style={{ fontSize: 10, padding: "4px 8px" }}>✕ Ninguna</button>
            </div>
          </div>

          {cargandoTarifas && (
            <div style={{ padding: "32px", textAlign: "center", color: "var(--text3)", fontFamily: "var(--mono)", fontSize: 12 }}>
              Cargando tarifas...
            </div>
          )}

          {errorTarifas && (
            <div style={{
              padding: "14px 18px", color: "var(--red)", fontSize: 13,
              background: "rgba(255,91,91,.06)", borderBottom: "1px solid var(--border)",
              lineHeight: 1.6
            }}>
              ⚠ <strong>No se pudieron cargar las tarifas</strong><br />
              <span style={{ color: "var(--text2)", fontSize: 12 }}>
                Asegúrate de que el backend está arrancado con{" "}
                <code style={{ fontFamily: "var(--mono)", background: "rgba(255,255,255,.06)", padding: "1px 6px", borderRadius: 4 }}>
                  py -3.11 -m uvicorn main:app --reload
                </code>{" "}
                y de que la API Key en <code style={{ fontFamily: "var(--mono)", background: "rgba(255,255,255,.06)", padding: "1px 6px", borderRadius: 4 }}>.env.development</code> coincide con la del backend.
              </span>
            </div>
          )}

          {!cargandoTarifas && !errorTarifas && tarifas.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <div className="empty-text">No hay tarifas cargadas</div>
            </div>
          )}

          {tarifas.map((t, i) => (
            <div key={i}>
              <div className="tarifa-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div onClick={() => toggleDetalle(i)} style={{ flex: 1, cursor: "pointer" }}>
                  <div className="tarifa-comercial">{t.nombre_tarifa}</div>
                  <div className="tarifa-nombre">{t.comercializadora}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button className={`toggle-btn ${t._activa ? "on" : "off"}`}
                    onClick={() => toggleActiva(i)}>
                    <span className="toggle-dot"></span>
                    {t._activa ? "ACTIVA" : "INACTIVA"}
                  </button>
                  <span className={`tarifa-chevron ${abiertos[i] ? "open" : ""}`}
                    onClick={() => toggleDetalle(i)} style={{ cursor: "pointer" }}>▼</span>
                </div>
              </div>
              {abiertos[i] && (
                <div className="tarifa-detalle">
                  <div className="detalle-item">
                    <div className="detalle-label">Pot. P1 (€/kW·día)</div>
                    <div className="detalle-valor">{t.precio_potencia_p1}</div>
                  </div>
                  <div className="detalle-item">
                    <div className="detalle-label">Pot. P2 (€/kW·día)</div>
                    <div className="detalle-valor">{t.precio_potencia_p2}</div>
                  </div>
                  <div className="detalle-item">
                    <div className="detalle-label">Energía P1 (€/kWh)</div>
                    <div className="detalle-valor">{t.precio_energia_p1}</div>
                  </div>
                  <div className="detalle-item">
                    <div className="detalle-label">Energía P2 (€/kWh)</div>
                    <div className="detalle-valor">{t.precio_energia_p2}</div>
                  </div>
                  <div className="detalle-item">
                    <div className="detalle-label">Energía P3 (€/kWh)</div>
                    <div className="detalle-valor">{t.precio_energia_p3}</div>
                  </div>
                  <div className="detalle-item">
                    <div className="detalle-label">Descuento</div>
                    <div className="detalle-valor neutral">{t.descuento_pct || 0}%</div>
                  </div>
                  <div className="detalle-item">
                    <div className="detalle-label">Permanencia</div>
                    <div className="detalle-valor neutral">
                      {!t.permanencia_meses || t.permanencia_meses === 0 ? "Sin permanencia" : `${t.permanencia_meses} meses`}
                    </div>
                  </div>
                  {t.enlace_contratacion && (
                    <div className="detalle-item" style={{ gridColumn: "1 / -1" }}>
                      <div className="detalle-label">Enlace contratación</div>
                      <a href={t.enlace_contratacion} target="_blank" rel="noopener noreferrer"
                        style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--blue)" }}>
                        {t.enlace_contratacion}
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── APP PRINCIPAL ─────────────────────────────────────────────────────────────
export default function Panel() {
  const [usuario, setUsuario] = useState(null);
  const [seccion, setSeccion] = useState("leads");
  const [temaClaro, setTemaClaro] = useState(false);

  useEffect(() => {
    document.body.className = temaClaro ? "tema-claro" : "";
  }, [temaClaro]);

  if (!usuario) return (
    <>
      <style>{css}</style>
      <Login onLogin={setUsuario} />
    </>
  );

  const secciones = {
    leads:   { label: "Leads",    icon: "👥", titulo: "Gestión de Leads" },
    tarifas: { label: "Tarifas",  icon: "📊", titulo: "Gestión de Tarifas" },
  };

  return (
    <>
      <style>{css}</style>
      <div className="panel-wrapper">
        {/* SIDEBAR */}
        <aside className="sidebar">
          <div className="sidebar-logo">
            <img src={temaClaro ? logoNeronClaro : logoNeron} alt="Nerón Energía"
              style={{ width: 180, height: 80, objectFit: "contain", display: "block", marginBottom: 4 }} />
            <div className="sidebar-sub" style={{ textAlign: "center" }}>Panel interno</div>
          </div>
          <nav className="sidebar-nav">
            <div className="nav-section">Gestión</div>
            {Object.entries(secciones).map(([key, { label, icon }]) => (
              <button key={key} className={`nav-item ${seccion === key ? "active" : ""}`}
                onClick={() => setSeccion(key)}>
                <span className="nav-icon">{icon}</span>
                {label}
              </button>
            ))}
          </nav>
          <div className="sidebar-footer">
            <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 8, fontFamily: "var(--mono)" }}>
              {usuario.email}
            </div>
            <button className="theme-toggle" onClick={() => setTemaClaro(t => !t)}>
              {temaClaro ? "🌙 Tema oscuro" : "☀️ Tema claro"}
            </button>
            <button className="logout-btn" onClick={async () => {
              await supabase.auth.signOut();
              setUsuario(null);
            }}>Cerrar sesión</button>
          </div>
        </aside>

        {/* MAIN */}
        <div className="panel-main">
          <div className="panel-topbar">
            <div className="topbar-title">{secciones[seccion].titulo}</div>
            <div className="topbar-badge">PANEL INTERNO · NERÓN ENERGÍA</div>
          </div>
          {/* NAVBAR MÓVIL */}
          <div className="mobile-nav">
            {Object.entries(secciones).map(([key, { label, icon }]) => (
              <button key={key} className={`mobile-nav-btn ${seccion === key ? "active" : ""}`}
                onClick={() => setSeccion(key)}>
                {icon} {label}
              </button>
            ))}
            <button className="mobile-nav-btn" onClick={() => setTemaClaro(t => !t)}
              style={{ flex: "0 0 auto" }}>
              {temaClaro ? "🌙" : "☀️"}
            </button>
          </div>

          <div className="panel-content">
            {seccion === "leads"   && <SeccionLeads />}
            {seccion === "tarifas" && <SeccionTarifas />}
          </div>
        </div>
      </div>
    </>
  );
}
